import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money, xaf } from "@/lib/format";
import { toast } from "sonner";
import { getRoute, reverseGeocode } from "@/lib/geo";
import { GeoMap } from "@/components/GeoMap";
import { AddressInput } from "@/components/AddressInput";
import { LoyaltyBadge } from "@/components/LoyaltyBadge";
import {
  Car, MapPin, NavigationArrow, Star, X, Path, SteeringWheel, User as UserIcon,
  CheckCircle, FileArrowUp, Clock, Wallet, Money,
} from "@phosphor-icons/react";

const ACTIVE = ["scheduled", "requested", "accepted", "arriving", "in_progress"];
const STATUS_LABEL = {
  scheduled: "Scheduled",
  requested: "Finding you a driver…",
  accepted: "Driver is on the way",
  arriving: "Driver is arriving",
  in_progress: "On the trip",
  completed: "Trip completed",
  cancelled: "Trip cancelled",
};

function StatusBadge({ status }) {
  const tone = status === "completed" ? "bg-emerald-500/15 text-emerald-600"
    : status === "cancelled" ? "bg-destructive/15 text-destructive"
    : "bg-primary/15 text-primary";
  return <span data-testid="ride-status" className={`rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}>{STATUS_LABEL[status] || status}</span>;
}

/* ------------------------------------------------------------------ Rider */
function Rider({ config }) {
  const token = config.mapbox_token;
  const qc = useQueryClient();
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [routeGeo, setRouteGeo] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [prox, setProx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("car");
  const [pay, setPay] = useState("wallet");
  const [promo, setPromo] = useState("");
  const [promoDisc, setPromoDisc] = useState(0);
  const [scheduleAt, setScheduleAt] = useState("");
  const cats = config.categories || [];
  const xafRate = config.xaf_per_usd;

  const applyPromo = async () => {
    if (!promo || !estimate) return;
    try {
      const d = (await api.get(`/promos/validate?code=${encodeURIComponent(promo)}&scope=ride&amount_cents=${estimate.fare_cents}`)).data;
      setPromoDisc(d.discount_cents); toast.success(`Promo applied — you save ${money(d.discount_cents)}`);
    } catch (e) { setPromoDisc(0); toast.error(apiError(e)); }
  };

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(async (p) => {
      const lng = p.coords.longitude, lat = p.coords.latitude;
      setProx([lng, lat]);
      const addr = await reverseGeocode(lng, lat, token);
      setPickup((cur) => cur || { lng, lat, address: addr || "Current location" });
    }, () => {}, { enableHighAccuracy: true, timeout: 8000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: mine } = useQuery({
    queryKey: ["rides-mine"],
    queryFn: async () => (await api.get("/rides/mine")).data.rides,
    refetchInterval: 4000,
  });
  const activeRide = (mine || []).find((r) => ACTIVE.includes(r.status));
  const lastRide = (mine || [])[0];
  const shown = activeRide || (lastRide && lastRide.status === "completed" && !lastRide.rated ? lastRide : null);

  const { data: ride } = useQuery({
    enabled: !!shown?.id,
    queryKey: ["ride", shown?.id],
    queryFn: async () => (await api.get(`/rides/${shown.id}`)).data.ride,
    refetchInterval: shown && ACTIVE.includes(shown.status) ? 3000 : false,
    initialData: shown,
  });
  const live = ride || shown;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (pickup && dropoff && token) {
        try { const g = await getRoute(pickup, dropoff, token); if (!cancelled) setRouteGeo(g); } catch {}
        try { const e = (await api.post("/rides/estimate", { pickup, dropoff, category })).data; if (!cancelled) setEstimate(e); } catch {}
      } else { setRouteGeo(null); setEstimate(null); }
    })();
    return () => { cancelled = true; };
  }, [pickup, dropoff, token, category]);

  const request = async () => {
    if (!pickup || !dropoff) { toast.error("Set a pickup and a destination"); return; }
    setBusy(true);
    try {
      await api.post("/rides/request", { pickup, dropoff, category, payment_method: pay,
        promo_code: pay === "wallet" ? promo : "", scheduled_at: scheduleAt ? new Date(scheduleAt).toISOString() : null });
      toast.success(scheduleAt ? "Ride scheduled" : "Ride requested — finding a driver");
      setPromo(""); setPromoDisc(0); setScheduleAt("");
      qc.invalidateQueries({ queryKey: ["rides-mine"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!live) return;
    setBusy(true);
    try { await api.post(`/rides/${live.id}/status`, { status: "cancelled" }); toast("Ride cancelled"); qc.invalidateQueries({ queryKey: ["rides-mine"] }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const rate = async (stars) => {
    try { await api.post(`/rides/${live.id}/rate`, { stars }); toast.success("Thanks for rating!"); qc.invalidateQueries({ queryKey: ["rides-mine"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const mapPickup = live ? live.pickup : pickup;
  const mapDropoff = live ? live.dropoff : dropoff;
  const markers = [
    mapPickup && { key: "pickup", lat: mapPickup.lat, lng: mapPickup.lng, color: "#16a34a" },
    mapDropoff && { key: "dropoff", lat: mapDropoff.lat, lng: mapDropoff.lng, color: "#dc2626" },
    live?.driver_lat != null && { key: "driver", lat: live.driver_lat, lng: live.driver_lng, color: "#2563eb" },
  ].filter(Boolean);

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
      <GeoMap token={token} markers={markers} routeGeo={live ? null : routeGeo} />
      <div className="space-y-4">
        {!live && (
          <div className="space-y-4 rounded-2xl border border-border bg-card p-5" data-testid="ride-book-panel">
            <h2 className="font-display text-lg font-semibold">Where to?</h2>
            <div className="grid grid-cols-3 gap-2" data-testid="ride-categories">
              {cats.map((c) => (
                <button key={c.key} data-testid={`ride-cat-${c.key}`} onClick={() => setCategory(c.key)}
                  className={`rounded-xl border p-2.5 text-center transition-colors ${category === c.key ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}>
                  {c.key === "bike" ? "🏍️" : c.key === "truck" ? "🚚" : "🚗"}
                  <p className="mt-0.5 text-xs font-semibold">{c.label}</p>
                </button>
              ))}
            </div>
            <AddressInput label="Pickup" token={token} proximity={prox} value={pickup} onSelect={setPickup} testid="ride-pickup" />
            <AddressInput label="Destination" token={token} proximity={prox} value={dropoff} onSelect={setDropoff} testid="ride-dropoff" />
            {estimate && (
              <div className="space-y-3 rounded-xl bg-background p-4" data-testid="ride-estimate">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Path size={16} /> {estimate.distance_km} km
                    {config.surge?.active && <span className="ml-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600">Surge {config.surge.multiplier}×</span>}
                  </span>
                  <div className="text-right">
                    <span className="font-display text-2xl font-semibold" data-testid="ride-fare">{money(estimate.fare_cents)}</span>
                    <p className="text-xs text-muted-foreground">{xaf(estimate.fare_cents, xafRate)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <input data-testid="ride-promo" value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Promo code"
                    className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-sm uppercase outline-none focus:border-primary" />
                  <button data-testid="ride-promo-apply" onClick={applyPromo} className="rounded-xl border border-border px-4 text-sm font-semibold">Apply</button>
                </div>
                {promoDisc > 0 && pay === "wallet" && <div className="flex justify-between text-sm text-emerald-600"><span>Promo discount</span><span>−{money(promoDisc)}</span></div>}
                <label className="block text-xs font-medium text-muted-foreground">Schedule for later (optional)
                  <input data-testid="ride-schedule" type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-primary" />
                </label>
              </div>
            )}
            <div className="flex gap-2" data-testid="ride-pay-toggle">
              {[["wallet", "Wallet", Wallet], ["cash", "Cash", Money]].map(([v, l, Icon]) => (
                <button key={v} data-testid={`pay-${v}`} onClick={() => setPay(v)}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${pay === v ? "border-primary bg-primary/10" : "border-border"}`}>
                  <Icon size={15} /> {l}
                </button>
              ))}
            </div>
            <button data-testid="ride-request-btn" onClick={request} disabled={busy || !pickup || !dropoff}
              className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-50">
              {busy ? "Requesting…" : (() => {
                const net = Math.max(0, (estimate?.fare_cents || 0) - (pay === "wallet" ? promoDisc : 0));
                if (!estimate) return "Request ride";
                return `${scheduleAt ? "Schedule ride" : "Request ride"} · ${money(net)}`;
              })()}
            </button>
          </div>
        )}

        {live && (
          <div className="space-y-4 rounded-2xl border border-border bg-card p-5" data-testid="ride-active-panel">
            <div className="flex items-center justify-between">
              <StatusBadge status={live.status} />
              <span className="font-display text-lg font-semibold" data-testid="ride-active-fare">{money(live.charged_cents ?? live.fare_cents)}</span>
            </div>
            {live.scheduled_at && <p className="text-xs text-muted-foreground">Scheduled for {new Date(live.scheduled_at).toLocaleString()}</p>}
            {live.driver_name && (
              <div className="flex items-center gap-3 rounded-xl bg-background p-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/15 text-primary"><SteeringWheel size={22} weight="duotone" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{live.driver_name}</p>
                  <p className="truncate text-xs text-muted-foreground">{live.vehicle || "Vehicle"} {live.vehicle_plate ? `· ${live.vehicle_plate}` : ""}</p>
                </div>
              </div>
            )}
            <div className="space-y-2 text-sm">
              <p className="flex items-start gap-2"><MapPin size={15} weight="fill" className="mt-0.5 text-emerald-600" /> <span className="line-clamp-1">{live.pickup?.address || "Pickup"}</span></p>
              <p className="flex items-start gap-2"><MapPin size={15} weight="fill" className="mt-0.5 text-destructive" /> <span className="line-clamp-1">{live.dropoff?.address || "Destination"}</span></p>
            </div>
            {ACTIVE.includes(live.status) && (
              <button data-testid="ride-cancel-btn" onClick={cancel} disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-semibold text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50">
                <X size={15} /> Cancel ride
              </button>
            )}
            {live.status === "completed" && !live.rated && (
              <div className="rounded-xl bg-background p-4 text-center" data-testid="ride-rating">
                <p className="text-sm font-medium">Rate your trip</p>
                <div className="mt-2 flex justify-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} data-testid={`ride-star-${n}`} onClick={() => rate(n)}
                      className="text-amber-400 transition-transform hover:scale-125"><Star size={28} weight="fill" /></button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Driver */
function DriverRegister({ onDone, categories = [] }) {
  const [f, setF] = useState({ vehicle_make: "", vehicle_model: "", vehicle_color: "", vehicle_plate: "", vehicle_type: "car", vehicle_year: "", license_number: "", license_expiry: "", state_id_number: "", vin_chassis: "", insurance_number: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.vehicle_make || !f.vehicle_plate) { toast.error("Add at least your vehicle make and plate"); return; }
    setBusy(true);
    try { await api.post("/rides/driver/register", f); toast.success("Registered — now upload your documents for approval"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const inp = (k, ph) => (
    <input data-testid={`driver-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph}
      className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />
  );
  return (
    <div className="mx-auto max-w-md space-y-4 rounded-2xl border border-border bg-card p-6" data-testid="driver-register">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary"><SteeringWheel size={22} weight="duotone" /></div>
        <div>
          <h2 className="font-display text-lg font-semibold">Transporter &amp; Vehicle Registration</h2>
          <p className="text-sm text-muted-foreground">Register to carry passengers or goods. Approval by a community inspector is required.</p>
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground">Vehicle type</p>
        <div className="mt-1 grid grid-cols-3 gap-2">
          {categories.map((c) => (
            <button key={c.key} data-testid={`driver-type-${c.key}`} onClick={() => setF({ ...f, vehicle_type: c.key })}
              className={`rounded-xl border p-2.5 text-center transition-colors ${f.vehicle_type === c.key ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}>
              {c.key === "bike" ? "🏍️" : c.key === "truck" ? "🚚" : "🚗"}
              <p className="mt-0.5 text-xs font-semibold">{c.label}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">{inp("vehicle_make", "Make (Toyota)")}{inp("vehicle_model", "Model (Corolla)")}</div>
      <div className="grid grid-cols-2 gap-3">{inp("vehicle_color", "Color (White)")}{inp("vehicle_year", "Year (2018)")}</div>
      <div className="grid grid-cols-2 gap-3">{inp("vehicle_plate", "Plate (LT-123-AB)")}{inp("vin_chassis", "VIN / Chassis no.")}</div>
      <div className="grid grid-cols-2 gap-3">
        {inp("license_number", "Driver's license no.")}
        <input data-testid="driver-license_expiry" type="date" value={f.license_expiry} onChange={(e) => setF({ ...f, license_expiry: e.target.value })}
          className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-muted-foreground outline-none focus:border-primary" />
      </div>
      <div className="grid grid-cols-2 gap-3">{inp("state_id_number", "State Photo ID no.")}{inp("insurance_number", "Insurance policy no.")}</div>
      <p className="text-xs text-muted-foreground">After registering you'll upload your documents (license, State Photo ID, registration, insurance, vehicle photo & a selfie) for verification and vehicle inspection before going online.</p>
      <button data-testid="driver-register-btn" onClick={submit} disabled={busy}
        className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? "Saving…" : "Register to drive"}</button>
    </div>
  );
}

function Driver({ config }) {
  const token = config.mapbox_token;
  const qc = useQueryClient();
  const [online, setOnline] = useState(config.driver?.status === "online");
  const [busy, setBusy] = useState(false);
  const watchRef = useRef(null);

  const { data: reqs } = useQuery({
    queryKey: ["driver-requests"],
    queryFn: async () => (await api.get("/rides/driver/requests")).data.rides,
    refetchInterval: online ? 5000 : false,
    enabled: online,
  });
  const { data: mine } = useQuery({
    queryKey: ["rides-mine"],
    queryFn: async () => (await api.get("/rides/mine")).data.rides,
    refetchInterval: 4000,
  });
  const active = (mine || []).find((r) => ACTIVE.includes(r.status) && r.driver_id);

  useEffect(() => {
    if (active && navigator.geolocation) {
      watchRef.current = navigator.geolocation.watchPosition((p) => {
        api.post(`/rides/${active.id}/location`, { lat: p.coords.latitude, lng: p.coords.longitude }).catch(() => {});
      }, () => {}, { enableHighAccuracy: true, maximumAge: 5000 });
    }
    return () => { if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; } };
  }, [active?.id]);

  const toggleOnline = async (val) => {
    setBusy(true);
    const send = (lat, lng) => api.post("/rides/driver/online", { online: val, lat, lng });
    try {
      if (val && navigator.geolocation) {
        await new Promise((res) => navigator.geolocation.getCurrentPosition(
          async (p) => { await send(p.coords.latitude, p.coords.longitude); res(); },
          async () => { await send(null, null); res(); }, { enableHighAccuracy: true, timeout: 8000 }));
      } else { await send(null, null); }
      setOnline(val);
      toast.success(val ? "You're online" : "You're offline");
      qc.invalidateQueries({ queryKey: ["driver-requests"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const accept = async (id) => {
    setBusy(true);
    try { await api.post(`/rides/${id}/accept`); toast.success("Ride accepted"); qc.invalidateQueries({ queryKey: ["rides-mine"] }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const advance = async (status) => {
    setBusy(true);
    try { await api.post(`/rides/${active.id}/status`, { status }); qc.invalidateQueries({ queryKey: ["rides-mine"] }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const nextAction = active && {
    accepted: { label: "I've arrived", status: "arriving" },
    arriving: { label: "Start trip", status: "in_progress" },
    in_progress: { label: "Complete trip", status: "completed" },
  }[active.status];

  const markers = active ? [
    active.pickup && { key: "pickup", lat: active.pickup.lat, lng: active.pickup.lng, color: "#16a34a" },
    active.dropoff && { key: "dropoff", lat: active.dropoff.lat, lng: active.dropoff.lng, color: "#dc2626" },
    active.driver_lat != null && { key: "driver", lat: active.driver_lat, lng: active.driver_lng, color: "#2563eb" },
  ].filter(Boolean) : [];

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
      <GeoMap token={token} markers={markers} routeGeo={null} />
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-2xl border border-border bg-card p-4" data-testid="driver-status-panel">
          <div>
            <p className="text-sm font-semibold">{online ? "You're online" : "You're offline"}</p>
            <p className="text-xs text-muted-foreground">{online ? "Receiving ride requests" : "Go online to get rides"}</p>
          </div>
          <button data-testid="driver-online-toggle" onClick={() => toggleOnline(!online)} disabled={busy}
            className={`relative h-7 w-12 rounded-full transition-colors ${online ? "bg-emerald-500" : "bg-border"}`}>
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${online ? "left-6" : "left-1"}`} />
          </button>
        </div>

        <PayoutPanel />

        {active && (
          <div className="space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="driver-active-panel">
            <div className="flex items-center justify-between">
              <StatusBadge status={active.status} />
              <span className="font-display text-lg font-semibold text-emerald-600">+{money(active.driver_earnings_cents)}</span>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-background p-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary"><UserIcon size={20} weight="duotone" /></div>
              <div><p className="text-sm font-semibold">{active.rider_name}</p><p className="text-xs text-muted-foreground">{active.distance_km} km trip</p></div>
            </div>
            <div className="space-y-2 text-sm">
              <p className="flex items-start gap-2"><MapPin size={15} weight="fill" className="mt-0.5 text-emerald-600" /> <span className="line-clamp-1">{active.pickup?.address}</span></p>
              <p className="flex items-start gap-2"><MapPin size={15} weight="fill" className="mt-0.5 text-destructive" /> <span className="line-clamp-1">{active.dropoff?.address}</span></p>
            </div>
            {nextAction && (
              <button data-testid="driver-advance-btn" onClick={() => advance(nextAction.status)} disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                <NavigationArrow size={16} weight="fill" /> {nextAction.label}
              </button>
            )}
          </div>
        )}

        {!active && online && (
          <div className="space-y-3" data-testid="driver-requests">
            <p className="text-sm font-semibold">Ride requests</p>
            {(reqs || []).length === 0 && <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No requests yet. Stay online — new trips appear here.</p>}
            {(reqs || []).map((r) => (
              <div key={r.id} data-testid={`driver-request-${r.id}`} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{r.rider_name}</span>
                  <span className="font-display text-lg font-semibold text-emerald-600">+{money(r.driver_earnings_cents)}</span>
                </div>
                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                  <p className="flex items-start gap-1.5"><MapPin size={13} weight="fill" className="mt-0.5 text-emerald-600" /> <span className="line-clamp-1">{r.pickup?.address}</span></p>
                  <p className="flex items-start gap-1.5"><MapPin size={13} weight="fill" className="mt-0.5 text-destructive" /> <span className="line-clamp-1">{r.dropoff?.address}</span></p>
                  <p className="pt-1">{r.distance_km} km · fare {money(r.fare_cents)}</p>
                </div>
                <button data-testid={`driver-accept-${r.id}`} onClick={() => accept(r.id)} disabled={busy}
                  className="mt-3 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">Accept ride</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const DOC_DEFS = [
  { key: "license", label: "Driver's license" },
  { key: "state_id", label: "State Photo ID" },
  { key: "registration", label: "Vehicle registration" },
  { key: "insurance", label: "Insurance certificate" },
  { key: "vehicle_photo", label: "Vehicle photo" },
  { key: "selfie", label: "Driver selfie / photo" },
];

function DriverDocs({ documents = [], onUploaded }) {
  const [busy, setBusy] = useState("");
  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    try {
      const fd = new FormData(); fd.append("doc_type", key); fd.append("file", file);
      await api.post("/rides/driver/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onUploaded?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid="driver-docs">
      {DOC_DEFS.map((d) => {
        const done = documents.includes(d.key);
        return (
          <label key={d.key} data-testid={`driver-doc-${d.key}`}
            className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 text-sm hover:border-primary/40">
            <span className="flex items-center gap-2">
              {done ? <CheckCircle size={18} weight="fill" className="text-emerald-600" /> : <FileArrowUp size={18} className="text-muted-foreground" />}
              {d.label}
            </span>
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold">
              {busy === d.key ? "Uploading…" : done ? "Replace" : "Upload"}
            </span>
            <input type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => upload(d.key, e.target.files?.[0])} />
          </label>
        );
      })}
    </div>
  );
}

function DriverPending({ config, refetch }) {
  const d = config.driver || {};
  const rejected = d.approval_status === "rejected";
  return (
    <div className="mx-auto max-w-md space-y-4" data-testid="driver-pending">
      <div className={`rounded-2xl border p-5 ${rejected ? "border-destructive/40 bg-destructive/5" : "border-amber-500/40 bg-amber-500/5"}`}>
        <div className="flex items-center gap-2">
          {rejected ? <X size={20} className="text-destructive" /> : <Clock size={20} className="text-amber-600" weight="fill" />}
          <h2 className="font-display text-lg font-semibold">{rejected ? "Application not approved" : "Under review"}</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {rejected
            ? (d.inspection?.notes || "Your documents weren't approved. Please re-upload clear documents and we'll review again.")
            : "Thanks for signing up! Upload the documents below. Our team verifies them and inspects your vehicle before you can go online."}
        </p>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold">Your documents</p>
        <DriverDocs documents={d.documents} onUploaded={refetch} />
      </div>
      <button data-testid="driver-refresh" onClick={refetch} className="w-full rounded-full border border-border py-2.5 text-sm font-semibold transition-colors hover:border-primary">Refresh status</button>
    </div>
  );
}

function PayoutPanel() {
  const qc = useQueryClient();
  const [amt, setAmt] = useState("");
  const [num, setNum] = useState("");
  const [prov, setProv] = useState("mtn");
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["payouts-mine"], queryFn: async () => (await api.get("/payouts/mine")).data });
  const request = async () => {
    const cents = Math.round(Number(amt) * 100);
    if (!cents || !num) { toast.error("Enter an amount and your Mobile Money number"); return; }
    setBusy(true);
    try {
      await api.post("/payouts/request", { amount_cents: cents, momo_number: num, momo_provider: prov });
      toast.success("Payout requested — we'll send it to your Mobile Money");
      setAmt(""); setNum(""); qc.invalidateQueries({ queryKey: ["payouts-mine"] }); qc.invalidateQueries({ queryKey: ["wallet"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const tone = (s) => s === "paid" ? "text-emerald-600" : s === "rejected" ? "text-destructive" : "text-amber-600";
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="payout-panel">
      <h3 className="flex items-center gap-2 font-display text-base font-semibold"><Wallet size={18} weight="duotone" /> Cash out earnings</h3>
      <p className="mt-1 text-sm text-muted-foreground">Balance <span className="font-semibold text-foreground" data-testid="payout-balance">{money(data?.balance_cents)}</span></p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <input data-testid="payout-amount" type="number" step="0.5" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="Amount $"
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <select data-testid="payout-provider" value={prov} onChange={(e) => setProv(e.target.value)}
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
          <option value="mtn">MTN MoMo</option><option value="orange">Orange Money</option>
        </select>
        <input data-testid="payout-number" value={num} onChange={(e) => setNum(e.target.value)} placeholder="Mobile Money number"
          className="col-span-2 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
      </div>
      <button data-testid="payout-request-btn" onClick={request} disabled={busy}
        className="mt-3 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? "Requesting…" : "Request payout"}</button>
      <div className="mt-3 space-y-1">
        {(data?.payouts || []).slice(0, 5).map((p) => (
          <div key={p.id} className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{money(p.amount_cents)} · {p.momo_provider.toUpperCase()}</span>
            <span className={`font-semibold ${tone(p.status)}`}>{p.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Page */
export default function Rides() {
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("onboard") ? "drive" : "ride");
  const [isDriver, setIsDriver] = useState(false);
  const { data: config, isLoading, refetch } = useQuery({
    queryKey: ["rides-config"],
    queryFn: async () => (await api.get("/rides/config")).data,
  });
  useEffect(() => { if (config) setIsDriver(config.is_driver); }, [config]);

  if (isLoading) return <div className="text-muted-foreground">Loading…</div>;

  if (!config?.enabled) {
    return (
      <div className="mx-auto max-w-md rounded-2xl border border-dashed border-border p-10 text-center" data-testid="rides-disabled">
        <Car size={40} className="mx-auto text-muted-foreground" weight="duotone" />
        <h1 className="mt-3 font-display text-xl font-semibold">Rides are coming soon</h1>
        <p className="mt-1 text-sm text-muted-foreground">This feature isn't available yet. Check back shortly.</p>
      </div>
    );
  }
  if (!config?.mapbox_token) {
    return (
      <div className="mx-auto max-w-md rounded-2xl border border-dashed border-border p-10 text-center" data-testid="rides-no-map">
        <Car size={40} className="mx-auto text-muted-foreground" weight="duotone" />
        <h1 className="mt-3 font-display text-xl font-semibold">Map not configured</h1>
        <p className="mt-1 text-sm text-muted-foreground">The Maps token is missing. An admin needs to add it before rides can run.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rides-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">iYeneh Ride</h1>
          <p className="mt-1 text-muted-foreground">Book a ride or drive to earn — pay by wallet or cash.</p>
          <div className="mt-2"><LoyaltyBadge /></div>
        </div>
        <div className="flex rounded-full border border-border bg-card p-1">
          <button data-testid="tab-ride" onClick={() => setTab("ride")}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "ride" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            <UserIcon size={16} weight="bold" /> Ride
          </button>
          <button data-testid="tab-drive" onClick={() => setTab("drive")}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "drive" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            <SteeringWheel size={16} weight="bold" /> Drive
          </button>
        </div>
      </div>

      {tab === "ride"
        ? <Rider config={config} />
        : (!isDriver
            ? <DriverRegister categories={config.categories} onDone={() => { setIsDriver(true); refetch(); }} />
            : (config.driver?.approval_status === "approved"
                ? <Driver config={config} />
                : <DriverPending config={config} refetch={refetch} />))}
    </div>
  );
}
